import React, { useState, useEffect } from 'react';
import api from '../../api';

const STATUS_OPTS = ['vacant', 'occupied', 'cleaning', 'maintenance'];
const STATUS_COLOR = {
  occupied:    { bg: 'rgba(13,148,136,.12)',  text: '#0d9488' },
  vacant:      { bg: 'rgba(100,116,139,.10)', text: '#64748b' },
  cleaning:    { bg: 'rgba(245,158,11,.12)',  text: '#b45309' },
  maintenance: { bg: 'rgba(239,68,68,.12)',   text: '#dc2626' },
};
const TURNOVER_STATUS = {
  scheduled:   { bg: '#ede9fe', text: '#6d28d9' },
  in_progress: { bg: '#fef3c7', text: '#b45309' },
};

const INPUT_STYLE = { width: '100%', fontSize: 13, padding: '7px 10px', borderRadius: 7,
  border: '1px solid #e2e8f0', background: '#f8fafc', color: '#1e293b', boxSizing: 'border-box' };

// ── Quick Schedule Turnover Modal ─────────────────────────────────────────────
function QuickTurnoverModal({ unit, onSave, onClose }) {
  const [form, setForm] = useState({ checkout_date: '', checkin_date: '', assigned_to: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  async function handleSave() {
    setSaving(true);
    try { await api.post('/pm/turnovers', { ...form, unit_id: unit.id, type: 'clean' }); onSave(); }
    catch {} finally { setSaving(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 400, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 4, color: '#1e293b' }}>Schedule Turnover</div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
          {unit.property_name} · {unit.unit_number}{unit.name ? ` — ${unit.name}` : ''}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
          {[['checkout_date', 'Checkout Date'], ['checkin_date', 'Check-in Date']].map(([k, l]) => (
            <div key={k}>
              <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{l}</label>
              <input type="date" value={form[k]} onChange={set(k)} style={INPUT_STYLE} />
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Assigned To</label>
          <input value={form.assigned_to} onChange={set('assigned_to')} placeholder="Cleaner or staff" style={INPUT_STYLE} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Schedule'}</button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Unit Row ──────────────────────────────────────────────────────────────────
function UnitRow({ unit, turnover, onStatusChange, onSchedule }) {
  const [saving, setSaving] = useState(false);
  async function handleStatus(e) {
    setSaving(true);
    try { await api.put(`/pm/units/${unit.id}`, { status: e.target.value }); onStatusChange(); }
    catch {} finally { setSaving(false); }
  }
  const sc = STATUS_COLOR[unit.status] || STATUS_COLOR.vacant;
  const tc = turnover ? (TURNOVER_STATUS[turnover.status] || TURNOVER_STATUS.scheduled) : null;
  const done = turnover?.checklist?.filter(i => i.done).length ?? 0;
  const total = turnover?.checklist?.length ?? 0;

  return (
    <tr>
      <td style={{ fontWeight: 600, fontSize: 13, color: '#1e293b' }}>
        {unit.unit_number}{unit.name ? ` — ${unit.name}` : ''}
      </td>
      <td style={{ fontSize: 12, color: '#64748b' }}>
        {unit.bedrooms}br / {unit.bathrooms}ba{unit.max_guests ? ` · ${unit.max_guests} guests` : ''}
      </td>
      <td>
        <select value={unit.status} onChange={handleStatus} disabled={saving}
          style={{ fontSize: 12, padding: '3px 8px', borderRadius: 6, border: '1px solid #e2e8f0',
            background: sc.bg, color: sc.text, cursor: 'pointer' }}>
          {STATUS_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td>
        {turnover ? (
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            background: tc.bg, color: tc.text, whiteSpace: 'nowrap' }}>
            🧹 {turnover.status.replace('_', ' ')}{total > 0 ? ` ${done}/${total}` : ''}
          </span>
        ) : (
          <button onClick={onSchedule}
            style={{ fontSize: 11, color: '#0d9488', background: 'none', border: '1px dashed #0d9488',
              borderRadius: 4, padding: '2px 8px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            + Turnover
          </button>
        )}
      </td>
      <td style={{ fontSize: 12, color: '#64748b' }}>{unit.notes || '—'}</td>
    </tr>
  );
}

// ── Modals ────────────────────────────────────────────────────────────────────
function AddUnitModal({ propertyId, onSave, onClose }) {
  const [form, setForm] = useState({ unit_number: '', name: '', bedrooms: 1, bathrooms: 1, max_guests: '', status: 'vacant' });
  const [saving, setSaving] = useState(false);
  async function handleSave() {
    if (!form.unit_number) return;
    setSaving(true);
    try { await api.post('/pm/units', { ...form, property_id: propertyId }); onSave(); }
    catch {} finally { setSaving(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 380, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#1e293b' }}>Add Unit</div>
        {[
          { label: 'Unit Number *', key: 'unit_number', placeholder: '101' },
          { label: 'Unit Name', key: 'name', placeholder: 'Ocean Suite' },
          { label: 'Bedrooms', key: 'bedrooms', type: 'number' },
          { label: 'Bathrooms', key: 'bathrooms', type: 'number' },
          { label: 'Max Guests', key: 'max_guests', type: 'number' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{f.label}</label>
            <input type={f.type || 'text'} value={form[f.key]} placeholder={f.placeholder}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={INPUT_STYLE} />
          </div>
        ))}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.unit_number}>
            {saving ? 'Saving…' : 'Add Unit'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AddPropertyModal({ owners, onSave, onClose }) {
  const [form, setForm] = useState({ name: '', address: '', type: 'short_term_rental', owner_id: '' });
  const [saving, setSaving] = useState(false);
  async function handleSave() {
    if (!form.name) return;
    setSaving(true);
    try { await api.post('/pm/properties', form); onSave(); }
    catch {} finally { setSaving(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: 400, padding: 24 }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16, color: '#1e293b' }}>Add Property</div>
        {[
          { label: 'Property Name *', key: 'name', placeholder: 'Oceanfront Villa' },
          { label: 'Address', key: 'address', placeholder: '123 Beach Rd, Kailua-Kona, HI' },
        ].map(f => (
          <div key={f.key} style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>{f.label}</label>
            <input value={form[f.key]} placeholder={f.placeholder}
              onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
              style={INPUT_STYLE} />
          </div>
        ))}
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Type</label>
          <select value={form.type} onChange={e => setForm(p => ({ ...p, type: e.target.value }))}
            style={INPUT_STYLE}>
            <option value="short_term_rental">Short-Term Rental</option>
            <option value="long_term_rental">Long-Term Rental</option>
            <option value="commercial">Commercial</option>
          </select>
        </div>
        {owners.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: '#64748b', display: 'block', marginBottom: 4 }}>Owner</label>
            <select value={form.owner_id} onChange={e => setForm(p => ({ ...p, owner_id: e.target.value }))}
              style={INPUT_STYLE}>
              <option value="">— no owner —</option>
              {owners.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <button className="btn btn-primary btn-sm" onClick={handleSave} disabled={saving || !form.name}>
            {saving ? 'Saving…' : 'Add Property'}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Units Fetcher (loads units + active turnovers for a property) ──────────────
function UnitsFetcher({ propertyId, onRefresh, onScheduleTurnover }) {
  const [units, setUnits] = useState([]);
  const [turnovers, setTurnovers] = useState([]);

  function loadUnits() {
    api.get(`/pm/units?property_id=${propertyId}`).then(r => setUnits(r.data)).catch(() => {});
  }
  function loadTurnovers() {
    // fetch active turnovers across both statuses; filter by unit belonging to this property client-side
    Promise.all([
      api.get('/pm/turnovers?status=scheduled'),
      api.get('/pm/turnovers?status=in_progress'),
    ]).then(([s, ip]) => setTurnovers([...s.data, ...ip.data])).catch(() => {});
  }

  useEffect(() => { loadUnits(); loadTurnovers(); }, [propertyId]);

  const turnoverByUnit = Object.fromEntries(turnovers.map(t => [t.unit_id, t]));

  return units.map(u => (
    <UnitRow key={u.id} unit={u} turnover={turnoverByUnit[u.id] || null}
      onStatusChange={() => { loadUnits(); onRefresh(); }}
      onScheduleTurnover={() => onScheduleTurnover(u)} />
  ));
}

// ── Main View ─────────────────────────────────────────────────────────────────
export default function PropertiesView() {
  const [properties, setProperties] = useState([]);
  const [owners, setOwners] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [addUnitFor, setAddUnitFor] = useState(null);
  const [schedulingFor, setSchedulingFor] = useState(null); // unit object
  const [loading, setLoading] = useState(true);

  function load() {
    Promise.all([api.get('/pm/properties'), api.get('/pm/owners')])
      .then(([p, o]) => { setProperties(p.data); setOwners(o.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  if (loading) return <div className="loading">Loading properties…</div>;

  return (
    <>
      <div className="page-header">
        <span className="page-title">Properties</span>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAddProperty(true)}>+ Add Property</button>
      </div>
      <div className="page-body">
        {properties.length === 0 ? (
          <div className="card">
            <div className="card-body" style={{ textAlign: 'center', padding: 48, color: '#64748b' }}>
              No properties yet. Add your first property to get started.
            </div>
          </div>
        ) : properties.map(prop => (
          <div key={prop.id} className="card mb-3">
            <div className="card-body" style={{ padding: '14px 18px', cursor: 'pointer' }}
              onClick={() => setExpanded(e => ({ ...e, [prop.id]: !e[prop.id] }))}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 18 }}>🏠</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>{prop.name}</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                    {prop.address && <span>{prop.address} · </span>}
                    {prop.owner_name && <span>Owner: {prop.owner_name} · </span>}
                    <span>{prop.unit_count} unit{prop.unit_count !== 1 ? 's' : ''}</span>
                    {prop.occupied_count > 0 && <span style={{ color: '#0d9488' }}> · {prop.occupied_count} occupied</span>}
                    {prop.maintenance_count > 0 && <span style={{ color: '#dc2626' }}> · {prop.maintenance_count} maintenance</span>}
                  </div>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation();
                    if (window.confirm(`Delete "${prop.name}"? This cannot be undone.`)) {
                      api.delete(`/pm/properties/${prop.id}`).then(load).catch(() => {});
                    }
                  }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444',
                    fontSize: 16, padding: '2px 6px', lineHeight: 1 }}
                  title="Delete property">✕</button>
                <span style={{ color: '#94a3b8', fontSize: 12 }}>{expanded[prop.id] ? '▲' : '▼'}</span>
              </div>
            </div>

            {expanded[prop.id] && (
              <div style={{ borderTop: '1px solid #e2e8f0', padding: '12px 18px' }}>
                {prop.unit_count === 0 ? (
                  <div style={{ color: '#64748b', fontSize: 13, marginBottom: 12 }}>No units yet.</div>
                ) : (
                  <div className="table-wrap" style={{ marginBottom: 12 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>Unit</th>
                          <th>Beds / Baths</th>
                          <th>Status</th>
                          <th>Turnover</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        <UnitsFetcher
                          propertyId={prop.id}
                          onRefresh={load}
                          onScheduleTurnover={unit => setSchedulingFor({ ...unit, property_name: prop.name })}
                        />
                      </tbody>
                    </table>
                  </div>
                )}
                <button className="btn btn-ghost btn-sm" onClick={() => setAddUnitFor(prop.id)}>+ Add Unit</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {showAddProperty && (
        <AddPropertyModal owners={owners}
          onSave={() => { setShowAddProperty(false); load(); }}
          onClose={() => setShowAddProperty(false)} />
      )}
      {addUnitFor && (
        <AddUnitModal propertyId={addUnitFor}
          onSave={() => { setAddUnitFor(null); load(); }}
          onClose={() => setAddUnitFor(null)} />
      )}
      {schedulingFor && (
        <QuickTurnoverModal unit={schedulingFor}
          onSave={() => setSchedulingFor(null)}
          onClose={() => setSchedulingFor(null)} />
      )}
    </>
  );
}
